import { useRouter } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';

import { OptionRow, PrimaryCTA } from '@/components/onboarding/Controls';
import { Eyebrow, H1, Screen, WhyLine } from '@/components/onboarding/Screen';
import { Spacing } from '@/constants/theme';
import type { YearBand } from '@/db/types';
import { yearBandFromYearBuilt } from '@/db/persist-onboarding';
import { useOnboarding } from '@/store/onboarding';

const OPTIONS: { v: YearBand; title: string; sub?: string }[] = [
  { v: 'pre60', title: 'Before 1960' },
  { v: '60-80', title: '1960s – 1980s' },
  { v: '90-00', title: '1990s – 2000s' },
  { v: '2010+', title: '2010 or newer' },
  { v: 'unsure', title: 'Not sure', sub: "We'll use safe defaults" },
];

export default function YearScreen() {
  const router = useRouter();
  const setYearBand = useOnboarding((s) => s.setYearBand);
  const stored = useOnboarding((s) => s.yearBand);
  const yearBuilt = useOnboarding((s) => s.yearBuilt);

  // If the user typed a specific build year on the home-type screen,
  // skip this era picker entirely — we already have the precise number.
  // Derive the band from the year so legacy rule-engine paths that
  // still key on year_band keep working, then push to the next step.
  useEffect(() => {
    if (yearBuilt !== null) {
      const band = yearBandFromYearBuilt(yearBuilt);
      if (band) setYearBand(band);
      router.replace('/(onboarding)/size' as never);
    }
  }, [yearBuilt, router, setYearBand]);

  const [picked, setPicked] = useState<YearBand | null>(stored);
  const advancing = useRef(false);

  const go = (v: YearBand) => {
    if (advancing.current) return;
    advancing.current = true;
    setYearBand(v);
    router.push('/(onboarding)/size' as never);
    // Re-arm for users who navigate back to this screen.
    setTimeout(() => { advancing.current = false; }, 800);
  };

  // Tapping an era both selects it and advances (Wave 47 speed-up) —
  // this screen is a pure single-select, so a pick IS the answer. The
  // short delay lets the check animation paint first. Continue stays
  // for users who arrive with a stored pick and just want to move on.
  const pick = (v: YearBand) => {
    setPicked(v);
    setYearBand(v);
    setTimeout(() => go(v), 220);
  };

  const next = () => {
    if (!picked) return;
    go(picked);
  };

  // While the auto-skip effect is running we briefly render an empty
  // Screen — saves a flash of the era picker for users who already
  // typed a year on the previous screen.
  if (yearBuilt !== null) {
    return (
      <Screen>
        <View />
      </Screen>
    );
  }

  return (
    <Screen footer={<PrimaryCTA label="Continue" disabled={!picked} onPress={next} />}>
      <Eyebrow>3 · Your home</Eyebrow>
      <H1>Roughly when{'\n'}was it built?</H1>
      <WhyLine>
        Older systems need attention more often — this helps us guess what
        you&apos;ve got. Skip this one if you typed an exact year on the
        previous screen.
      </WhyLine>
      <View style={{ marginTop: Spacing.five, gap: Spacing.two }}>
        {OPTIONS.map((o) => (
          <OptionRow
            key={o.v}
            title={o.title}
            subtitle={o.sub}
            selected={picked === o.v}
            onPress={() => pick(o.v)}
          />
        ))}
      </View>
    </Screen>
  );
}
